import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAuth } from '../../hooks/useAuth';
import { Sidebar } from './Sidebar';
import { CustomerList } from '../customers/CustomerList';
import { LeadsList } from '../leads/LeadsList';
import { LeadDetail } from '../leads/LeadDetail';
import { DashboardHome } from '../dashboard/DashboardHome';
import { PagesList } from '../pages/PagesList';
import { PageDetail } from '../pages/PageDetail';
import { PageBuilderV2 } from '../page-builder/PageBuilderV2';
import { TemplatePageBuilder } from '../page-builder/TemplatePageBuilder';
import { AIBuilder } from '../ai-builder/AIBuilder';
import type { Lead } from '../../api/leadsApi';
import type { LandingPage } from '../../api/pagesApi';
import '../../api/leadsApi';
import '../../api/pagesApi';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  display: flex;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 0;

  @media (min-width: 768px) {
    margin-left: 250px;
  }
`;

const Header = styled.header`
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  color: #4a5568;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const PageTitle = styled.h1`
  color: #333;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserInfo = styled.div`
  display: none;
  flex-direction: column;
  align-items: flex-end;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.span`
  font-weight: 500;
  color: #333;
`;

const UserRole = styled.span`
  font-size: 12px;
  color: #666;
  text-transform: capitalize;
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background: #c82333;
  }
`;

const Main = styled.main`
  flex: 1;
  background-color: #f8f9fa;
  overflow-x: hidden;
`;

const PlaceholderPage = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  color: #718096;
`;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = (): string => {
    const path = location.pathname;
    if (path.includes('/customers')) return 'customers';
    if (path.includes('/leads')) return 'leads';
    if (path.includes('/template-builder')) return 'template-builder';
    if (path.includes('/pages-v2')) return 'pages-v2';
    if (path.includes('/ai-builder')) return 'ai-builder';
    if (path.includes('/pages')) return 'pages';
    return 'dashboard';
  };

  const getPageTitle = () => {
    const activeTab = getActiveTab();
    switch (activeTab) {
      case 'customers':
        return 'Customers';
      case 'leads':
        return 'Leads';
      case 'pages':
        return 'Landing Pages';
      case 'pages-v2':
        return 'Page Builder';
      case 'template-builder':
        return 'Template Builder';
      case 'ai-builder':
        return 'AI Builder';
      default:
        return 'Dashboard';
    }
  };

  const handleTabChange = (tab: string) => {
    navigate(`/dashboard/${tab === 'dashboard' ? '' : tab}`);
  };

  const handleNavigate = (tab: string) => {
    if (tab === 'create-page') {
      navigate('/dashboard/template-builder/create');
    } else {
      navigate(`/dashboard/${tab === 'dashboard' ? '' : tab}`);
    }
  };

  return (
    <DashboardContainer>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeTab={getActiveTab()}
        onTabChange={handleTabChange}
      />
      
      <MainContent>
        <Header>
          <HeaderLeft>
            <MobileMenuButton onClick={() => setSidebarOpen(true)}>
              ☰
            </MobileMenuButton>
            <PageTitle>{getPageTitle()}</PageTitle>
          </HeaderLeft>
          
          <UserSection>
            <UserInfo>
              <UserName>{user?.name}</UserName>
              <UserRole>{user?.role}</UserRole>
            </UserInfo>
            <LogoutButton onClick={logout}>
              Logout
            </LogoutButton>
          </UserSection>
        </Header>
        
        <Main>
          <Routes>
            <Route path="/" element={<DashboardHome onNavigate={handleNavigate} />} />
            <Route 
              path="/customers" 
              element={
                user?.role === 'admin' ? 
                  <CustomerList /> : 
                  <PlaceholderPage>Access denied. Admin role required.</PlaceholderPage>
              } 
            />
            <Route path="/leads/*" element={<LeadsRoutes />} />
            <Route path="/pages/*" element={<PagesRoutes />} />
            <Route path="/pages-v2/*" element={<PagesV2Routes />} />
            <Route path="/ai-builder/*" element={<AIBuilderRoutes />} />
            <Route path="/template-builder/*" element={<TemplateBuilderRoutes />} />
          </Routes>
        </Main>
      </MainContent>
    </DashboardContainer>
  );
};

const LeadsRoutes: React.FC = () => {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const handleLeadSelect = (lead: Lead) => {
    setSelectedLead(lead);
    navigate(`/dashboard/leads/${lead.id}`);
  };

  const handleCloseModal = () => {
    setSelectedLead(null);
    navigate('/dashboard/leads');
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<LeadsList onLeadSelect={handleLeadSelect} />} />
        <Route path="/:leadId" element={<LeadsList onLeadSelect={handleLeadSelect} />} />
      </Routes>
      
      <LeadDetail
        isOpen={!!leadId || !!selectedLead}
        lead={selectedLead}
        onClose={handleCloseModal}
        onEdit={(lead) => {
          handleCloseModal();
          console.log('Edit lead:', lead);
        }}
      />
    </>
  );
};

const PagesRoutes: React.FC = () => {
  const navigate = useNavigate();

  const handleCreatePage = () => {
    navigate('/dashboard/template-builder/create');
  };

  const handleEditPage = (page: LandingPage) => {
    navigate(`/dashboard/pages/edit/${page.id}`);
  };

  const handleBackToPages = () => {
    navigate('/dashboard/pages');
  };

  const handlePageSaved = (page: LandingPage) => {
    navigate('/dashboard/pages');
  };

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <PagesList
            onCreatePage={handleCreatePage}
            onEditPage={handleEditPage}
          />
        } 
      />
      <Route 
        path="/create" 
        element={
          <PageDetail
            pageId={undefined}
            onBack={handleBackToPages}
            onSave={handlePageSaved}
          />
        } 
      />
      <Route 
        path="/edit/:pageId" 
        element={<PageDetailRoute onBack={handleBackToPages} onSave={handlePageSaved} />} 
      />
    </Routes>
  );
};

const PageDetailRoute: React.FC<{ onBack: () => void; onSave: (page: LandingPage) => void }> = ({ onBack, onSave }) => {
  const { pageId } = useParams();
  
  return (
    <PageDetail
      pageId={pageId}
      onBack={onBack}
      onSave={onSave}
    />
  );
};

const PagesV2Routes: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard/pages-v2');
  };

  const handlePageSaved = (page: LandingPage) => {
    navigate('/dashboard/pages-v2');
  };

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <PageBuilderV2
            onBack={() => navigate('/dashboard/pages')}
            onSave={handlePageSaved}
          />
        } 
      />
      <Route 
        path="/create" 
        element={
          <PageBuilderV2
            onBack={handleBack}
            onSave={handlePageSaved}
          />
        } 
      />
      <Route 
        path="/edit/:pageId" 
        element={<PageBuilderV2Route onBack={handleBack} onSave={handlePageSaved} />} 
      />
    </Routes>
  );
};

const PageBuilderV2Route: React.FC<{ onBack: () => void; onSave: (page: LandingPage) => void }> = ({ onBack, onSave }) => {
  const { pageId } = useParams();
  
  return (
    <PageBuilderV2
      pageId={pageId}
      onBack={onBack}
      onSave={onSave}
    />
  );
};

const TemplateBuilderRoutes: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard/pages');
  };

  const handleSave = (data: any) => {
    // Handle saving template-based page
    console.log('Saving template page:', data);
    navigate('/dashboard/pages');
  };

  return (
    <Routes>
      <Route 
        path="/create" 
        element={
          <TemplatePageBuilder
            onBack={handleBack}
            onSave={handleSave}
          />
        } 
      />
    </Routes>
  );
};

const AIBuilderRoutes: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <AIBuilder
            onBack={handleBack}
          />
        } 
      />
    </Routes>
  );
};

export default Dashboard;